/**
 * Traffic generation from the airport's openScope spawn patterns.
 *
 * Arrivals: they appear where their STAR crosses the scope edge and fly it until it runs out
 * (then they need vectors); one with no STAR for its runway appears at the edge pointed roughly
 * at the airport. Departures appear holding short of the
 * departure runway with a requested exit fix from the SID; if the SID's exit is outside the
 * scope the last fix inside it is requested instead, so the handoff can happen on screen.
 * Timing is Poisson with the pattern rates scaled to the requested per-hour totals.
 */
import { bearingDeg, categoryForType, destination, distanceM, expandRoute, NM_TO_M, wrap180, type AirportData, type Fix, type RealismOptions, type RouteLeg, type RunwayEnd, type SpawnPattern } from '@atc/core';
import { fleetFor } from './airlines';
import { isRemovedPhase, type Aircraft } from './aircraft';
import { performanceFor, tasFromIas } from './performance';
import type { Rng } from './rng';
import { nearestGroundNode, taxiPath } from './surface';

export interface SpawnerOptions {
  scheduled?: boolean;
  scopeRadiusNm: number;
  arrivalsPerHour: number;
  departuresPerHour: number;
  /** Assumption: no more than this many departures wait at one runway. */
  maxQueuePerRunway: number;
  shortCallsigns?: boolean;
  /**
   * Seconds before the first *generated* arrival and departure, on top of the traffic already on
   * frequency. Without it a Poisson draw can land in the opening seconds and undo a quiet start.
   */
  openingQuietS?: { arrivals: number; departures: number };
  activeRunways?: { arrivals: string[]; departures: string[] };
}

/**
 * Assumption: a new aircraft appears at least this far from anything airborne at a similar level.
 * 10 nm gives about 70 s before two 250 kt aircraft meeting head-on lose 3 nm, time to react.
 */
export const SPAWN_CLEAR_NM = 10;
export const SPAWN_CLEAR_FT = 2000;

/**
 * Callsigns that are easy to confuse on the radio: identical, the same flight number on another
 * airline (ELY123/ISR123), or the same airline with one digit changed or the digits reordered
 * (ELY123/ELY124, ELY123/ELY132). Assumption: this simplified rule stands in for real
 * callsign-similarity programmes, which also weigh sound-alike letters and airline prefixes.
 */
export function similarCallsigns(a: string, b: string): boolean {
  if (a === b) return true;
  const pa = /^([A-Z]+)(\d+)/.exec(a), pb = /^([A-Z]+)(\d+)/.exec(b);
  if (!pa || !pb) return false;
  const [na, nb] = [pa[2]!, pb[2]!];
  if (na === nb) return true;
  if (pa[1] !== pb[1] || na.length !== nb.length) return false;
  const changed = [...na].filter((d, i) => d !== nb[i]).length;
  return changed <= 1 || [...na].sort().join('') === [...nb].sort().join('');
}

export class Spawner {
  private nextArrivalS = 0;
  private nextDepartureS = 0;
  private nextId = 1;
  private arrivalPatterns: SpawnPattern[];
  private departurePatterns: SpawnPattern[];

  constructor(private airport: AirportData, private rng: Rng, public options: SpawnerOptions) {
    this.arrivalPatterns = airport.spawnPatterns.filter((p) => p.category === 'arrival' && p.rateHourly > 0);
    this.departurePatterns = airport.spawnPatterns.filter((p) => p.category === 'departure' && p.rateHourly > 0);
    const quiet = options.openingQuietS ?? { arrivals: 0, departures: 0 };
    this.nextArrivalS = Math.max(quiet.arrivals, rng.exponentialS(options.arrivalsPerHour) * 0.5);
    this.nextDepartureS = Math.max(quiet.departures, rng.exponentialS(options.departuresPerHour) * 0.5);
  }

  /** Called every sim step; returns aircraft to add. */
  update(simTimeS: number, active: Aircraft[], opts: RealismOptions, maxAircraft: number): Aircraft[] {
    const out: Aircraft[] = [];
    if (active.length >= maxAircraft) return out;
    if (this.options.arrivalsPerHour > 0 && simTimeS >= this.nextArrivalS) {
      this.nextArrivalS = simTimeS + (this.options.scheduled ? 3600 / this.options.arrivalsPerHour : this.rng.exponentialS(this.options.arrivalsPerHour));
      const a = this.arrival(opts, active);
      if (a) out.push(a);
      else this.nextArrivalS = Math.min(this.nextArrivalS, simTimeS + 12);
    }
    if (this.options.departuresPerHour > 0 && simTimeS >= this.nextDepartureS) {
      this.nextDepartureS = simTimeS + (this.options.scheduled ? 3600 / this.options.departuresPerHour : this.rng.exponentialS(this.options.departuresPerHour));
      const d = this.departure(active);
      if (d) out.push(d);
      else this.nextDepartureS = Math.min(this.nextDepartureS, simTimeS + 20);
    }
    return out;
  }

  /**
   * Starting traffic so the scope is not empty. Arrivals are staggered inward from the scope
   * edge so they do not all need the runway at once; the caller decides how many of each.
   */
  initial(opts: RealismOptions, active: Aircraft[], counts: { arrivals: number; departures: number }): Aircraft[] {
    const out: Aircraft[] = [];
    for (let i = 0; i < counts.arrivals; i++) {
      const ac = this.arrival(opts, active, Math.max(0.4, 1 - 0.45 * i));
      if (ac) { out.push(ac); active = [...active, ac]; }
    }
    for (let i = 0; i < counts.departures; i++) {
      const ac = this.departure(active);
      if (ac) { out.push(ac); active = [...active, ac]; }
    }
    return out;
  }

  private callsign(pattern: SpawnPattern, active: Aircraft[]): { callsign: string; airline: string; type: string } {
    const al = pattern.airlines.length ? this.rng.weighted(pattern.airlines.map((a) => [a, a.weight] as const)) : { code: 'N', fleet: undefined, weight: 1 };
    const fleet = fleetFor(al.code, al.fleet, this.airport.xAtc.fleets);
    const type = this.rng.weighted(fleet);
    // Prefer a callsign that does not read like one already on frequency, then settle for a unique one.
    for (let tries = 0; tries < 40; tries++) {
      const callsign = al.code === 'N'
        ? `N${this.rng.int(10, 999)}${this.rng.pick(['AB', 'SF', 'TW', 'GK', 'PD', 'RM', 'CX', 'JL'])}`
        : `${al.code}${this.rng.int(this.options.shortCallsigns === false ? 1 : 10, this.options.shortCallsigns === false ? 1999 : 999)}`;
      if (!active.some((a) => (tries < 20 ? similarCallsigns(a.callsign, callsign) : a.callsign === callsign))) return { callsign, airline: al.code, type };
    }
    for (let n = 10; n <= 9999; n++) {
      const callsign = `${al.code}${n}`;
      if (!active.some((a) => a.callsign === callsign)) return { callsign, airline: al.code, type };
    }
    throw new Error('Callsign pool exhausted');
  }

  takeId(): number { return this.nextId++; }
  snapshot() { return { nextArrivalS: this.nextArrivalS, nextDepartureS: this.nextDepartureS, nextId: this.nextId, options: { ...this.options } }; }
  restore(state: ReturnType<Spawner['snapshot']>) { this.nextArrivalS = state.nextArrivalS; this.nextDepartureS = state.nextDepartureS; this.nextId = state.nextId; this.options = { ...state.options }; }

  private clearOfTraffic(lat: number, lon: number, altitudeFt: number, active: Aircraft[]): boolean {
    return active.every((o) => o.onGround || isRemovedPhase(o.phase) || Math.abs(o.altitudeFt - altitudeFt) >= SPAWN_CLEAR_FT || distanceM(o.lat, o.lon, lat, lon) >= SPAWN_CLEAR_NM * NM_TO_M);
  }

  private base(kind: Aircraft['kind'], id: { callsign: string; airline: string; type: string }, opts: RealismOptions): Aircraft {
    return blankAircraft(this.airport, opts, kind, id, this.takeId());
  }

  private routeLegs(route: string): RouteLeg[] {
    try { return expandRoute(this.airport, route).legs; } catch { return []; }
  }

  private procedureName(route: string): string | undefined {
    try { return expandRoute(this.airport, route).procedure?.name; } catch { return undefined; }
  }

  private routeFixes(route: string): Fix[] {
    let legs: RouteLeg[];
    try { legs = expandRoute(this.airport, route).legs; } catch { return []; }
    return legs.filter((l): l is Extract<RouteLeg, { kind: 'fix' }> => l.kind === 'fix').map((l) => this.airport.fixes.get(l.fix)).filter((f): f is Fix => !!f);
  }

  arrival(opts: RealismOptions, active: Aircraft[], radiusFraction = 1): Aircraft | null {
    const R = this.options.scopeRadiusNm * radiusFraction * NM_TO_M;
    const rw = this.balancedRunway('arrival', active);
    const compatible = rw ? this.arrivalPatterns.filter((p) => this.patternRunway(p) === rw.name) : [];
    const pool = compatible.length ? compatible : this.arrivalPatterns;
    const pattern = pool.length ? this.rng.weighted(pool.map((p) => [p, p.rateHourly] as const)) : null;
    const patternFits = !!pattern && this.patternRunway(pattern) === rw?.name;
    const ac = this.base('arrival', this.callsign(pattern ?? { origin: '', destination: '', category: 'arrival', route: '', method: 'random', rateHourly: 1, airlines: [] }, active), opts);
    ac.requested = 'Arrival';
    ac.arrivalRunway = rw;
    ac.flightPlan = patternFits ? this.routeLegs(pattern!.route) : [];
    const star = patternFits ? this.procedureName(pattern!.route) : undefined;
    if (star) ac.procedure = star;
    const fixes = pattern ? this.routeFixes(pattern.route) : [];
    const field = this.airport.elevationFt;

    // Where the STAR crosses the scope edge: every arrival with a STAR for its runway enters on it.
    let entryHeading: number | null = null;
    let legs: RouteLeg[] | null = null;
    let startIdx = 0;
    if (opts.arrivalsFollowStar && patternFits && pattern && fixes.length) {
      const allLegs = expandRoute(this.airport, pattern.route).legs;
      const entry = routeEntry(this.airport, allLegs, R);
      if (entry) { ac.lat = entry.lat; ac.lon = entry.lon; entryHeading = entry.headingDeg; startIdx = entry.routeIdx; legs = allLegs; }
    }
    if (entryHeading === null) {
      // No STAR for this runway: from the edge, roughly toward the field, along the direction the pattern comes from.
      const from = fixes.length ? bearingDeg(this.airport.lat, this.airport.lon, fixes[0]!.lat, fixes[0]!.lon) : this.rng.range(0, 360);
      const brg = (from + this.rng.range(-15, 15) + 360) % 360;
      const p = destination(this.airport.lat, this.airport.lon, brg, R);
      ac.lat = p.lat; ac.lon = p.lon;
      entryHeading = (bearingDeg(p.lat, p.lon, this.airport.lat, this.airport.lon) + this.rng.range(-20, 20) + 360) % 360;
    }
    const distNm = distanceM(ac.lat, ac.lon, this.airport.lat, this.airport.lon) / NM_TO_M;
    // Altitude: a 3:1 descent profile from here, capped by the pattern altitude, at least 5 000 AGL.
    const profile = Math.floor((field + distNm * 300) / 1000) * 1000;
    const patternAlt = pattern?.altitudeFt === undefined ? Infinity : Array.isArray(pattern.altitudeFt) ? pattern.altitudeFt[1] : pattern.altitudeFt;
    ac.altitudeFt = Math.max(field + 5000, Math.min(profile, patternAlt, this.airport.ctrCeilingFt, field + 12000));
    ac.altitudeFt = Math.round(ac.altitudeFt / 1000) * 1000;
    const runwayGapBlocked = () => !!rw && active.some((o) => o.kind === 'arrival' && o.arrivalRunway?.name === rw.name && !isRemovedPhase(o.phase) && !o.onGround && (
      distanceM(o.lat, o.lon, ac.lat, ac.lon) < SPAWN_CLEAR_NM * NM_TO_M ||
      Math.abs(distanceM(o.lat, o.lon, rw.lat, rw.lon) / Math.max(110, o.groundspeedKt) - distanceM(ac.lat, ac.lon, rw.lat, rw.lon) / Math.max(110, ac.perf.cruiseLowKt)) * 3600 / NM_TO_M < 100
    ));
    if (!this.clearOfTraffic(ac.lat, ac.lon, ac.altitudeFt, active) || runwayGapBlocked()) {
      // Try another level; if the entry point is busy at every level, skip this arrival.
      const base = ac.altitudeFt, ceiling = Math.max(this.airport.ctrCeilingFt, field + 12000);
      if (runwayGapBlocked()) return null;
      const alt = [base + 2000, base - 2000, base + 4000, base - 4000].find((a) => a >= field + 4000 && a <= ceiling && this.clearOfTraffic(ac.lat, ac.lon, a, active));
      if (alt === undefined) return null;
      ac.altitudeFt = alt;
    }
    ac.targetAltitudeFt = ac.altitudeFt;
    ac.headingDeg = entryHeading; ac.trackDeg = entryHeading;
    ac.iasKt = ac.altitudeFt <= 10000 ? ac.perf.cruiseLowKt : ac.perf.cruiseKt;
    // Groundspeed is true airspeed (still air until the first step): showing IAS would jump ~50 kt at 10 000 ft on the next tick.
    ac.groundspeedKt = tasFromIas(ac.iasKt, ac.altitudeFt);
    ac.nav = legs ? { mode: 'route', headingDeg: entryHeading, route: legs, routeIdx: startIdx } : { mode: 'heading', headingDeg: entryHeading, routeIdx: 0 };
    ac.phase = 'enroute';
    return ac;
  }

  departure(active: Aircraft[]): Aircraft | null {
    const rw = this.balancedRunway('departure', active);
    if (!rw) return null;
    const compatible = this.departurePatterns.filter((p) => this.patternRunway(p) === rw.name);
    const pool = compatible.length ? compatible : this.departurePatterns;
    const pattern = pool.length ? this.rng.weighted(pool.map((p) => [p, p.rateHourly] as const)) : null;
    const opts = { typeSpecificPerformance: true } as RealismOptions;
    const ac = this.base('departure', this.callsign(pattern ?? { origin: '', destination: '', category: 'departure', route: '', method: 'random', rateHourly: 1, airlines: [] }, active), opts);
    const queue = active.filter((a) => a.kind === 'departure' && a.departureRunway?.name === rw.name && (a.phase === 'taxiOut' || a.phase === 'holdShort' || a.phase === 'lineUp' || a.phase === 'linedUp' || a.phase === 'takeoffRoll' || a.phase === 'abortRoll'));
    if (queue.length >= this.options.maxQueuePerRunway) return null;
    // Requested exit: last SID fix inside the scope; else the nearest visible fix in the exit direction.
    const R = this.options.scopeRadiusNm * 0.9 * NM_TO_M;
    const fixes = pattern ? this.routeFixes(pattern.route) : [];
    // Only fixes drawn on the scope: "_" fixes are internal to openScope procedures.
    let requested = [...fixes].reverse().find((f) => !f.hidden && distanceM(f.lat, f.lon, this.airport.lat, this.airport.lon) < R);
    if (!requested) {
      const exitBrg = fixes.length ? bearingDeg(this.airport.lat, this.airport.lon, fixes[fixes.length - 1]!.lat, fixes[fixes.length - 1]!.lon) : this.rng.range(0, 360);
      let best: Fix | undefined, bestScore = Infinity;
      for (const f of this.airport.fixes.values()) {
        if (f.hidden) continue;
        const d = distanceM(f.lat, f.lon, this.airport.lat, this.airport.lon);
        if (d > R || d < 8 * NM_TO_M) continue;
        const score = Math.abs(wrap180(bearingDeg(this.airport.lat, this.airport.lon, f.lat, f.lon) - exitBrg)) - d / NM_TO_M;
        if (score < bestScore) { bestScore = score; best = f; }
      }
      requested = best;
    }
    ac.requested = requested?.name ?? '';
    const patternFits = !!pattern && this.patternRunway(pattern) === rw.name;
    const sid = patternFits ? this.routeLegs(pattern!.route) : [];
    const exitIdx = sid.findIndex((l) => l.kind === 'fix' && l.fix === requested?.name);
    ac.flightPlan = exitIdx >= 0 ? sid.slice(0, exitIdx + 1) : requested ? [{ kind: 'fix', fix: requested.name }] : [];
    const sidName = patternFits && exitIdx >= 0 ? this.procedureName(pattern!.route) : undefined;
    if (sidName) ac.procedure = sidName;
    // Hold short: beside the threshold on the left, queue further back, nose toward the runway.
    const back = Math.max(60, ...queue.map((a) => a.holdShortPos ? distanceM(rw.lat, rw.lon, a.holdShortPos.lat, a.holdShortPos.lon) + (this.options.scheduled ? 70 : 120) : 60));
    const hs = destination(rw.lat, rw.lon, (rw.headingDeg + 270) % 360, back);
    ac.lat = hs.lat; ac.lon = hs.lon;
    ac.headingDeg = (rw.headingDeg + 90) % 360; ac.trackDeg = ac.headingDeg;
    ac.holdShortPos = { lat: hs.lat, lon: hs.lon, headingDeg: ac.headingDeg };
    ac.altitudeFt = rw.elevationM / 0.3048;
    ac.onGround = true;
    const taxi = this.taxiRoute(rw, hs, active);
    if (taxi?.length) {
      ac.lat = taxi[0]!.lat; ac.lon = taxi[0]!.lon;
      const next = taxi[1]; if (next) ac.headingDeg = ac.trackDeg = bearingDeg(ac.lat, ac.lon, next.lat, next.lon);
      ac.taxiRoute = taxi.slice(1); ac.taxiRouteIdx = 0; ac.phase = 'taxiOut';
    } else ac.phase = 'holdShort';
    ac.departureRunway = rw;
    ac.nav = departureNav(ac, rw);
    ac.targetAltitudeFt = ac.altitudeFt;
    return ac;
  }

  private patternRunway(pattern: SpawnPattern): string | undefined {
    return pattern.route.match(new RegExp(`${this.airport.icao}(\\d{1,2}[LRC]?)`))?.[1];
  }

  private balancedRunway(kind: 'arrival' | 'departure', active: Aircraft[]): RunwayEnd | undefined {
    const ends = this.airport.runways.flatMap((r) => r.ends);
    const names = this.options.activeRunways?.[kind === 'arrival' ? 'arrivals' : 'departures'];
    const choices = (names?.length ? names : [kind === 'arrival' ? this.airport.arrivalRunway : this.airport.departureRunway]).map((n) => ends.find((e) => e.name === n)).filter((e): e is RunwayEnd => !!e);
    if (!choices.length) return ends[0];
    const load = (rw: RunwayEnd) => active.filter((a) => !isRemovedPhase(a.phase) && (kind === 'arrival' ? a.kind === 'arrival' && a.arrivalRunway?.name === rw.name : a.kind === 'departure' && a.departureRunway?.name === rw.name)).length;
    return choices.reduce((best, rw) => load(rw) < load(best) ? rw : best, choices[0]!);
  }

  private taxiRoute(rw: RunwayEnd, hold: { lat: number; lon: number }, active: Aircraft[]): { lat: number; lon: number }[] | null {
    const net = this.airport.groundNetwork;
    if (!net?.nodes.length || !net.stands.length) return null;
    const target = nearestGroundNode(net, hold);
    const used = new Set(active.filter((a) => a.phase === 'taxiOut').map((a) => nearestGroundNode(net, a)));
    const starts = net.stands.filter((s) => !used.has(s));
    const start = this.rng.pick(starts.length ? starts : net.stands);
    const path = taxiPath(net, start, target);
    if (!path || pathLengthM(path) < 80) return null;
    return [...path, hold];
  }
}

function blankAircraft(airport: AirportData, opts: RealismOptions, kind: Aircraft['kind'], id: { callsign: string; airline: string; type: string }, num: number): Aircraft {
  return {
    pilot: { heard: [], pending: [], history: [] },
    id: num, callsign: id.callsign, airline: id.airline, type: id.type, category: categoryForType(id.type), kind,
    perf: performanceFor(id.type, opts.typeSpecificPerformance),
    lat: airport.lat, lon: airport.lon, altitudeFt: airport.elevationFt, headingDeg: 0, trackDeg: 0, iasKt: 0, groundspeedKt: 0, verticalSpeedFpm: 0,
    onGround: false, phase: 'enroute', phaseT: 0, ageS: 0,
    nav: { mode: 'heading', headingDeg: 0, routeIdx: 0 },
    targetAltitudeFt: airport.elevationFt, expedite: false, targetIasKt: null,
    assigned: {}, requested: '', flightPlan: [], rollAfterLineUp: false, conflict: false, conflictWarning: false, lastRequestS: -Infinity,
  };
}

/** Explicit placement for scenarios and tests. */
export type AircraftSpec =
  | { kind: 'arrival' | 'overflight'; callsign: string; type?: string; lat: number; lon: number; altitudeFt: number; headingDeg: number; iasKt?: number; requested?: string; route?: string[]; runway?: string }
  /**
   * An arrival on its STAR: `nmBefore` miles before fix `at` (0 or absent: at it, heading for the next
   * fix), flying the STAR from there, at the lowest altitude the chart allows at `at` unless
   * `altitudeFt` says otherwise. `entry` picks the STAR's entry point (default: its first).
   */
  | { kind: 'arrival'; callsign: string; type?: string; star: string; at: string; nmBefore?: number; entry?: string; altitudeFt?: number; iasKt?: number; runway?: string }
  | { kind: 'departure'; callsign: string; type?: string; runway?: string; requested?: string; queueIndex?: number };

/**
 * The SID a departure from `runway` files to reach `requested`: every leg from the runway up to and
 * including that fix, from the first SID for the runway whose route passes it. Undefined when no
 * SID for the runway does.
 */
export function sidTo(airport: AirportData, runway: string, requested: string): { legs: RouteLeg[]; procedure: string } | undefined {
  const key = `${airport.icao}${runway}`;
  for (const p of airport.sids.values()) {
    if (!(key in p.rwy)) continue;
    for (const exit of Object.keys(p.exitPoints)) {
      let legs: RouteLeg[];
      try { legs = expandRoute(airport, `${key}.${p.icao}.${exit}`).legs; } catch { continue; }
      const idx = legs.findIndex((l) => l.kind === 'fix' && l.fix === requested);
      if (idx >= 0) return { legs: legs.slice(0, idx + 1), procedure: p.name };
    }
  }
  return undefined;
}

/** A departure's navigation before take-off: its filed route (the SID), flown once past 400 ft; else runway heading. */
function departureNav(ac: Aircraft, rw: RunwayEnd): Aircraft['nav'] {
  return ac.flightPlan.length ? { mode: 'route', headingDeg: rw.headingDeg, route: ac.flightPlan, routeIdx: 0 } : { mode: 'heading', headingDeg: rw.headingDeg, routeIdx: 0 };
}

/** Where on a route to put an aircraft: its position, the heading along the route there, and the leg to fly next. */
export interface RoutePlacement { lat: number; lon: number; headingDeg: number; routeIdx: number }

/**
 * `nmBefore` miles before fix `at` along `legs`, on the leg that leads to it (extended back past
 * the previous fix when the leg is shorter). At the fix itself it is heading for the next one.
 */
export function placeOnRoute(airport: AirportData, legs: readonly RouteLeg[], at: string, nmBefore = 0): RoutePlacement | undefined {
  const idx = legs.findIndex((l) => l.kind === 'fix' && l.fix === at);
  const fix = airport.fixes.get(at);
  if (idx < 0 || !fix) return undefined;
  const prev = legs.slice(0, idx).reverse().find((l) => l.kind === 'fix' && airport.fixes.has(l.fix));
  const from = prev?.kind === 'fix' ? airport.fixes.get(prev.fix)! : undefined;
  const after = legs.slice(idx + 1).find((l) => l.kind === 'fix' && airport.fixes.has(l.fix));
  const to = after?.kind === 'fix' ? airport.fixes.get(after.fix)! : undefined;
  if (nmBefore <= 0 || !from) {
    const headingDeg = to ? bearingDeg(fix.lat, fix.lon, to.lat, to.lon) : from ? bearingDeg(from.lat, from.lon, fix.lat, fix.lon) : bearingDeg(fix.lat, fix.lon, airport.lat, airport.lon);
    return { lat: fix.lat, lon: fix.lon, headingDeg, routeIdx: idx + 1 };
  }
  const inbound = bearingDeg(from.lat, from.lon, fix.lat, fix.lon);
  const p = destination(fix.lat, fix.lon, (inbound + 180) % 360, nmBefore * NM_TO_M);
  return { lat: p.lat, lon: p.lon, headingDeg: inbound, routeIdx: idx };
}

/** Where a route crosses into a circle of `radiusM` round the airport, flying inward; undefined when it never does. */
export function routeEntry(airport: AirportData, legs: readonly RouteLeg[], radiusM: number): RoutePlacement | undefined {
  const fixes = legs.flatMap((l, i) => { const f = l.kind === 'fix' ? airport.fixes.get(l.fix) : undefined; return f ? [{ f, i }] : []; });
  const inside = fixes.findIndex(({ f }) => distanceM(f.lat, f.lon, airport.lat, airport.lon) < radiusM);
  if (inside < 0) return undefined;
  if (inside === 0) {
    const { f, i } = fixes[0]!, next = fixes[1]?.f;
    return { lat: f.lat, lon: f.lon, headingDeg: next ? bearingDeg(f.lat, f.lon, next.lat, next.lon) : bearingDeg(f.lat, f.lon, airport.lat, airport.lon), routeIdx: i + 1 };
  }
  const a = fixes[inside - 1]!.f, { f: b, i } = fixes[inside]!;
  const brg = bearingDeg(a.lat, a.lon, b.lat, b.lon);
  let lo = 0, hi = distanceM(a.lat, a.lon, b.lat, b.lon);
  for (let n = 0; n < 24; n++) { const mid = (lo + hi) / 2; const p = destination(a.lat, a.lon, brg, mid); if (distanceM(p.lat, p.lon, airport.lat, airport.lon) > radiusM) lo = mid; else hi = mid; }
  const p = destination(a.lat, a.lon, brg, hi);
  return { lat: p.lat, lon: p.lon, headingDeg: brg, routeIdx: i };
}

/** The legs of `star` from its entry point to `runway` (as filed), or undefined when it is not a STAR here. */
export function starLegs(airport: AirportData, star: string, runway: string, entry?: string): RouteLeg[] | undefined {
  const p = airport.stars.get(star);
  if (!p) return undefined;
  const from = entry ?? Object.keys(p.entryPoints)[0];
  try { return expandRoute(airport, `${from}.${star}.${airport.icao}${runway}`).legs; } catch { return undefined; }
}

export function createAircraft(airport: AirportData, opts: RealismOptions, spec: AircraftSpec, id: number): Aircraft {
  const airline = /^N\d/.test(spec.callsign) ? 'N' : spec.callsign.replace(/\d.*$/, '');
  const type = spec.type ?? 'B738';
  const ac = blankAircraft(airport, opts, spec.kind, { callsign: spec.callsign, airline, type }, id);
  if (spec.kind === 'departure') {
    const rw = airport.runways.flatMap((r) => r.ends).find((e) => e.name === (spec.runway ?? airport.departureRunway)) ?? airport.runways[0]!.ends[0];
    const hs = destination(rw.lat, rw.lon, (rw.headingDeg + 270) % 360, 60 + (spec.queueIndex ?? 0) * 80);
    ac.lat = hs.lat; ac.lon = hs.lon; ac.headingDeg = (rw.headingDeg + 90) % 360; ac.trackDeg = ac.headingDeg;
    ac.holdShortPos = { lat: hs.lat, lon: hs.lon, headingDeg: ac.headingDeg };
    ac.altitudeFt = rw.elevationM / 0.3048; ac.targetAltitudeFt = ac.altitudeFt;
    ac.onGround = true; ac.phase = 'holdShort'; ac.departureRunway = rw;
    ac.requested = spec.requested ?? '';
    const sid = spec.requested ? sidTo(airport, rw.name, spec.requested) : undefined;
    ac.flightPlan = sid?.legs ?? (spec.requested ? [{ kind: 'fix', fix: spec.requested }] : []);
    if (sid) ac.procedure = sid.procedure;
    ac.nav = departureNav(ac, rw);
  } else if ('star' in spec) {
    const runway = spec.runway ?? airport.arrivalRunway;
    ac.arrivalRunway = airport.runways.flatMap((r) => r.ends).find((e) => e.name === runway);
    const legs = starLegs(airport, spec.star, runway, spec.entry);
    const at = legs && placeOnRoute(airport, legs, spec.at, spec.nmBefore);
    if (!legs || !at) throw new Error(`${spec.at} is not on the ${spec.star} at ${airport.icao}`);
    const leg = legs.find((l) => l.kind === 'fix' && l.fix === spec.at);
    const r = leg?.kind === 'fix' ? leg.restriction : undefined;
    const altitudeFt = spec.altitudeFt ?? r?.altitudeFt ?? r?.altitudeMinFt ?? r?.altitudeMaxFt ?? Math.round((airport.elevationFt + 6000) / 1000) * 1000;
    ac.lat = at.lat; ac.lon = at.lon; ac.altitudeFt = altitudeFt; ac.targetAltitudeFt = altitudeFt;
    ac.headingDeg = at.headingDeg; ac.trackDeg = at.headingDeg;
    ac.iasKt = spec.iasKt ?? (altitudeFt < 10000 ? ac.perf.cruiseLowKt : ac.perf.cruiseKt); ac.groundspeedKt = tasFromIas(ac.iasKt, ac.altitudeFt);
    ac.requested = 'Arrival';
    ac.flightPlan = legs;
    ac.procedure = airport.stars.get(spec.star)!.name;
    ac.nav = { mode: 'route', headingDeg: at.headingDeg, route: legs, routeIdx: at.routeIdx };
    ac.phase = 'enroute';
  } else {
    if (spec.kind === 'arrival') ac.arrivalRunway = airport.runways.flatMap((r) => r.ends).find((e) => e.name === (spec.runway ?? airport.arrivalRunway));
    ac.lat = spec.lat; ac.lon = spec.lon; ac.altitudeFt = spec.altitudeFt; ac.targetAltitudeFt = spec.altitudeFt;
    ac.headingDeg = spec.headingDeg; ac.trackDeg = spec.headingDeg;
    ac.iasKt = spec.iasKt ?? (spec.altitudeFt < 10000 ? ac.perf.cruiseLowKt : ac.perf.cruiseKt); ac.groundspeedKt = tasFromIas(ac.iasKt, ac.altitudeFt);
    ac.requested = spec.requested ?? 'Arrival';
    ac.flightPlan = (spec.route ?? []).map((fix) => ({ kind: 'fix', fix }));
    ac.nav = { mode: 'heading', headingDeg: spec.headingDeg, routeIdx: 0 };
    ac.phase = 'enroute';
  }
  return ac;
}

const pathLengthM = (path: readonly { lat: number; lon: number }[]) => path.slice(1).reduce((sum, p, i) => sum + distanceM(path[i]!.lat, path[i]!.lon, p.lat, p.lon), 0);
